"use client";

import { Program } from "@/common/program/types";
import { ProcessedCourse } from "@/common/course/types";
import useClientEnrollments from "@/lib/partner-api/useClientEnrollments";
import useClientEntitlements from "@/lib/partner-api/useClientEntitlements";
import useEmailOptIn from "@/components/ProgramDetailLayout/templates/context/ProgramDetailContext/hooks/useEmailOptIn";
import useEnrollmentUrl from "@/lib/program/utils/programEnroll/useEnrollmentUrl";

type Props = {
  programData: Program;
  courses: ProcessedCourse[];
  skus: string[];
  programIsEnrollable: boolean;
};

/**
 * Returns the enrollment URL for the current-program CTA in the compare tool,
 * or `null` when the CTA should be hidden.
 *
 * Returns `null` when:
 * - `programIsEnrollable` is false (enrollment window is closed / program is not active).
 * - `programData.programEligibleForOneClickPurchase` is false (program requires a
 *   separate admissions flow and does not support direct enrollment).
 *
 * This mirrors the guard used on the PDP's primary enrollment button so the
 * compare tool CTA is always consistent with the page-level enrollment state.
 */
export function useProgramCompareCtaUrl({
  programData,
  courses,
  skus,
  programIsEnrollable,
}: Props): string | null {
  const { enrollments } = useClientEnrollments();
  const { entitlements } = useClientEntitlements();
  const { emailOptIn } = useEmailOptIn();

  const enrollmentUrl = useEnrollmentUrl(
    programData,
    courses,
    skus,
    enrollments,
    entitlements,
    emailOptIn,
  );

  if (!programIsEnrollable || !programData.programEligibleForOneClickPurchase) {
    return null;
  }

  return enrollmentUrl;
}

export default useProgramCompareCtaUrl;
